import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  onSnapshot,
  limit,
  Timestamp
} from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase/config';
import { CoachProfile, VerificationStats, Notification } from '../types';
// Email API helper function
const sendEmailViaAPI = async (type: string, coach: any, rejectionReason?: string, notes?: string) => {
  try {
    const response = await fetch('/api/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type,
        coach,
        rejectionReason,
        notes,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to send email');
    }

    const result = await response.json();
    console.log(`Email sent successfully: ${type} to ${coach.email}`);
    return result;
  } catch (error) {
    console.error(`Failed to send ${type} email:`, error);
    throw error;
  }
};

// Function to send admin notification for new verifications
export const sendNewVerificationNotification = async (coach: CoachProfile) => {
  try {
    await sendEmailViaAPI('new_verification', coach);
    console.log(`Admin notification sent for new verification: ${coach.firstName} ${coach.lastName}`);
  } catch (error) {
    console.error('Failed to send admin notification:', error);
    // Don't throw error - notification failure shouldn't block other operations
  }
};

// Authentication Services
export const loginAdmin = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    throw error;
  }
};

export const logoutAdmin = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    throw error;
  }
};

export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Firestore Services
export const getPendingVerifications = async (): Promise<CoachProfile[]> => {
  try {
    console.log('Fetching pending verifications...');
    
    // Query for coaches with verificationStatus: 'pending' AND coachVerified: false
    const q = query(
      collection(db, 'coachProfile'),
      where('verificationStatus', '==', 'pending'),
      where('coachVerified', '==', false),
      orderBy('verificationSubmittedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    console.log('Found pending verifications:', querySnapshot.size);
    
    const coaches = querySnapshot.docs.map(doc => {
      const data = doc.data();
      console.log('Coach data:', { id: doc.id, ...data });
      return {
        id: doc.id,
        ...data
      };
    }) as CoachProfile[];
    
    console.log('Processed coaches:', coaches);
    return coaches;
  } catch (error) {
    console.error('Error fetching pending verifications:', error);
    throw error;
  }
};

export const getVerificationStats = async (): Promise<VerificationStats> => {
  try {
    console.log('Fetching verification stats...');
    
    const pendingQuery = query(
      collection(db, 'coachProfile'),
      where('verificationStatus', '==', 'pending'),
      where('coachVerified', '==', false)
    );
    
    const approvedQuery = query(
      collection(db, 'coachProfile'),
      where('verificationStatus', '==', 'approved')
    );
    
    const rejectedQuery = query(
      collection(db, 'coachProfile'),
      where('verificationStatus', '==', 'rejected')
    );
    
    const [pendingSnapshot, approvedSnapshot, rejectedSnapshot] = await Promise.all([
      getDocs(pendingQuery),
      getDocs(approvedQuery),
      getDocs(rejectedQuery)
    ]);
    
    // Get recent submissions (last 24 hours)
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const recentQuery = query(
      collection(db, 'coachProfile'),
      where('verificationSubmittedAt', '>=', Timestamp.fromDate(yesterday))
    );
    const recentSnapshot = await getDocs(recentQuery);
    
    const stats = {
      total: pendingSnapshot.size + approvedSnapshot.size + rejectedSnapshot.size,
      pending: pendingSnapshot.size,
      approved: approvedSnapshot.size,
      rejected: rejectedSnapshot.size,
      recentSubmissions: recentSnapshot.size
    };
    
    console.log('Verification stats:', stats);
    return stats;
  } catch (error) {
    console.error('Error fetching verification stats:', error);
    throw error;
  }
};

export const approveCoach = async (coachId: string, adminNotes?: string) => {
  try {
    const coachRef = doc(db, 'coachProfile', coachId);
    
    // Get the coach profile first to send email
    const coachDoc = await getDocs(collection(db, 'coachProfile'));
    const coach = coachDoc.docs.find(doc => doc.id === coachId);
    
    if (!coach) {
      throw new Error('Coach not found');
    }
    
    const coachData = {
      id: coach.id,
      ...coach.data()
    } as CoachProfile;
    
    // Update the coach profile
    await updateDoc(coachRef, {
      verificationStatus: 'approved',
      coachVerified: true,
      adminNotes: adminNotes || '',
      lastUpdated: Timestamp.now()
    });
    
    // Send approval email
    try {
      await sendEmailViaAPI('approval', coachData);
    } catch (emailError) {
      console.error('Failed to send approval email:', emailError);
      // Don't throw error - the approval was successful, email failure shouldn't block it
    }
  } catch (error) {
    throw error;
  }
};

export const rejectCoach = async (coachId: string, rejectionReason: string, adminNotes?: string) => {
  try {
    const coachRef = doc(db, 'coachProfile', coachId);
    
    // Get the coach profile first to send email
    const coachDoc = await getDocs(collection(db, 'coachProfile'));
    const coach = coachDoc.docs.find(doc => doc.id === coachId);
    
    if (!coach) {
      throw new Error('Coach not found');
    }
    
    const coachData = {
      id: coach.id,
      ...coach.data()
    } as CoachProfile;
    
    // Update the coach profile
    await updateDoc(coachRef, {
      verificationStatus: 'rejected',
      coachVerified: false,
      rejectionReason,
      adminNotes: adminNotes || '',
      lastUpdated: Timestamp.now()
    });
    
    // Send rejection email
    try {
      await sendEmailViaAPI('rejection', coachData, rejectionReason);
    } catch (emailError) {
      console.error('Failed to send rejection email:', emailError);
      // Don't throw error - the rejection was successful, email failure shouldn't block it
    }
  } catch (error) {
    throw error;
  }
};

export const requestMoreInfo = async (coachId: string, notes: string) => {
  try {
    const coachRef = doc(db, 'coachProfile', coachId);
    
    // Get the coach profile first to send email
    const coachDoc = await getDocs(collection(db, 'coachProfile'));
    const coach = coachDoc.docs.find(doc => doc.id === coachId);
    
    if (!coach) {
      throw new Error('Coach not found');
    }
    
    const coachData = {
      id: coach.id,
      ...coach.data()
    } as CoachProfile;
    
    // Update the coach profile
    await updateDoc(coachRef, {
      adminNotes: notes,
      lastUpdated: Timestamp.now()
    });
    
    // Send request info email
    try {
      await sendEmailViaAPI('request_info', coachData, undefined, notes);
    } catch (emailError) {
      console.error('Failed to send request info email:', emailError);
      // Don't throw error - the update was successful, email failure shouldn't block it
    }
  } catch (error) {
    throw error;
  }
};

export const getCoachProfile = async (coachId: string): Promise<CoachProfile | null> => {
  try {
    const coachDoc = await getDocs(collection(db, 'coachProfile'));
    const coach = coachDoc.docs.find(doc => doc.id === coachId);
    
    if (coach) {
      return {
        id: coach.id,
        ...coach.data()
      } as CoachProfile;
    }
    return null;
  } catch (error) {
    throw error;
  }
};

export const searchCoaches = async (searchTerm: string): Promise<CoachProfile[]> => {
  try {
    const q = query(
      collection(db, 'coachProfile'),
      where('verificationStatus', '==', 'pending'),
      where('coachVerified', '==', false)
    );
    
    const querySnapshot = await getDocs(q);
    const coaches = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as CoachProfile[];
    
    return coaches.filter(coach => 
      coach.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coach.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coach.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coach.baseballOrganization.toLowerCase().includes(searchTerm.toLowerCase())
    );
  } catch (error) {
    throw error;
  }
};

// Storage Services
export const getDocumentURL = async (path: string): Promise<string> => {
  try {
    const storageRef = ref(storage, path);
    return await getDownloadURL(storageRef);
  } catch (error) {
    throw error;
  }
};

// Debug function to see all coach profiles
export const getAllCoachProfiles = async (): Promise<CoachProfile[]> => {
  try {
    console.log('Fetching ALL coach profiles for debugging...');
    
    const q = query(collection(db, 'coachProfile'));
    const querySnapshot = await getDocs(q);
    
    console.log('Total coach profiles found:', querySnapshot.size);
    
    const coaches = querySnapshot.docs.map(doc => {
      const data = doc.data();
      console.log('Coach profile:', { id: doc.id, ...data });
      return {
        id: doc.id,
        ...data
      };
    }) as CoachProfile[];
    
    return coaches;
  } catch (error) {
    console.error('Error fetching all coach profiles:', error);
    throw error;
  }
};

// Get all coach profiles (for the verification queue with filters)
export const getAllCoachProfilesForQueue = async (): Promise<CoachProfile[]> => {
  try {
    console.log('Fetching all coach profiles for verification queue...');
    
    const q = query(
      collection(db, 'coachProfile'),
      orderBy('verificationSubmittedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    console.log('Total coach profiles found:', querySnapshot.size);
    
    const coaches = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data
      };
    }) as CoachProfile[];
    
    return coaches;
  } catch (error) {
    console.error('Error fetching all coach profiles:', error);
    throw error;
  }
};

// Real-time listeners
export const subscribeToPendingVerifications = (
  callback: (coaches: CoachProfile[]) => void
) => {
  const q = query(
    collection(db, 'coachProfile'),
    where('verificationStatus', '==', 'pending'),
    where('coachVerified', '==', false),
    orderBy('verificationSubmittedAt', 'desc')
  );
  
  return onSnapshot(q, (querySnapshot) => {
    const coaches = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as CoachProfile[];
    callback(coaches);
  });
};

export const subscribeToNotifications = (
  callback: (notifications: Notification[]) => void
) => {
  const q = query(
    collection(db, 'notifications'),
    orderBy('timestamp', 'desc'),
    limit(10)
  );
  
  return onSnapshot(q, (querySnapshot) => {
    const notifications = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Notification[];
    callback(notifications);
  });
}; 